import React, { useState } from 'react';
import { TextField, Button, List, ListItem, IconButton, Grid, Typography, Select,
    MenuItem, Checkbox, FormControlLabel, Box, FormControl, InputLabel, CircularProgress,
    Autocomplete, ListItemButton, } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Insurance, InsuranceStatus, InsuranceType, InsurancePolicyHolder
} from '../../../../models/insurance';
import { API as ZiphyAPI, InsuranceProvider } from '../../../../utils/ZiphyAPI'; 
import { useQuery } from '@tanstack/react-query';
import { Patient } from '../../../../models/core';
import DoneIcon from '@mui/icons-material/Done';
import AddIcon from '@mui/icons-material/Add';
import { DateTime } from 'luxon';

export const InsuranceTypeMap = [
    [InsuranceType.MEDICAL, 'Medical'],
    [InsuranceType.MEDICAID, 'Medicaid'],
    [InsuranceType.MEDICARE, 'Medicare'],
    [InsuranceType.TRICARE, 'Tricare'],
    [InsuranceType.CHAMPVA, 'Champva'],
    [InsuranceType.GROUP, 'Group'],
    [InsuranceType.FECA, 'Feca'],
    [InsuranceType.BLACK_LUNG, 'Black Lung'],
] as const;

export const InsurancePolicyHolderMap = [
    [InsurancePolicyHolder.SELF, 'Self'],
    [InsurancePolicyHolder.SPOUSE, 'Spouse'],
    [InsurancePolicyHolder.GUARDIAN, 'Guardian'],
    [InsurancePolicyHolder.GUARANTOR, 'Guarantor'],
    [InsurancePolicyHolder.OTHER, 'Other'],
] as const;

interface ProviderMethodReturn {
    insurance_providers:{ count:number, items:InsuranceProvider[] };
}

export function isValidMedicareId(raw: string): boolean {
  const mbi = raw.toUpperCase().replace(/[-\s]/g, '');

  if (mbi.length !== 11) return false;

  const L = 'ACDEFGHJKMNPQRTUVWXY';
  const AN = `0-9${L}`;

  const pattern = new RegExp(
    `^[1-9][${L}][${AN}][0-9][${L}][${AN}][0-9][${L}]{2}[0-9]{2}$`
  );

  return pattern.test(mbi);
}


function useInsuranceProviderQuery(requestMethod?:(offset:number, limit:number) => Promise<ProviderMethodReturn>) {
    return useQuery({
        queryKey: ['insurance_providers.list'],
        queryFn: async () => {
            let allItems: InsuranceProvider[] = [];
            const limit = 1000;
            let count = 0;

            try {
                let retries = 0;
                do {
                    try {
                        const {insurance_providers:{ count: currentCount, items }}
                            = requestMethod && await requestMethod(allItems.length, limit)
                            || await ZiphyAPI.getInsuranceProviderList(allItems.length, limit);
                        allItems = allItems.concat(items);
                        count = currentCount;
                    } catch(err) {
                        console.error('Failed: ', err);
                        retries += 1;
                        if(retries >= 3) {
                            throw err;
                        }
                    }
                } while (allItems.length < count);
            } catch (error) {
                console.error("Failed to fetch all insurance providers:", error);
                throw error;
            }

            return allItems;
        },
        select: (data) => { return data?.sort((a, b) => a.name.localeCompare(b.name)) || [];   },
        staleTime:14400_000
    });
}

interface Props {
    title?:string;
    patient?:Partial<Patient>;
    size?:'subtitle1'|'body2'|undefined;
    hasBorder?:boolean;
    insurance?:Insurance[];
    controlled?:boolean;
    readonly?:boolean;
    onUpdate?:(list:Insurance[]) => void;
    requestInsuranceProviders?:(offset:number, limit:number) => Promise<ProviderMethodReturn>;
}

const InsuranceList: React.FC<Props> = ({title, patient, hasBorder, insurance:insList, size,
    controlled = false, readonly = false, onUpdate, requestInsuranceProviders}:Props) => {
    const [localInsurances, setInsurances] = useState<Insurance[]>(insList || []);
    const insurances = controlled ? insList || [] : localInsurances;

    const [newInsurance, setNewInsurance] = useState<Insurance>({
        providerName: '',
        type: InsuranceType.MEDICAL,
        status: InsuranceStatus.ACTIVE,
        policyHolder: InsurancePolicyHolder.SELF,
        memberFirstName: patient?.firstName || '',
        memberLastName: patient?.lastName || '',
        isPrimary: false,
        created: DateTime.now().toISO()
    });

    const [editingInsurance, setEditingInsurance] = useState<Insurance | null>(null);

    const insuranceProviderQuery = useInsuranceProviderQuery(requestInsuranceProviders);
    const insuranceProviderList = React.useMemo(() => { return insuranceProviderQuery?.data || [] }, [insuranceProviderQuery]);
    const insuraceProviderNames = React.useMemo(() => { return insuranceProviderList.map((provider) => provider.name) }, [insuranceProviderList]);

    const [providerName, setProviderName] = React.useState<string>('');
    const [currentProvider, setCurrentProvider] = React.useState<InsuranceProvider|undefined>();

    React.useEffect(()=>{
        setNewInsurance(prev => ({...prev, ...{
            member_first_name:patient?.firstName || '',
            member_last_name:patient?.lastName || ''
        }}))
    },[patient?.firstName, patient?.lastName]);

    const handleEdit = (insurance: Insurance) => {
        setEditingInsurance(insurance);
        setNewInsurance(insurance);
        setCurrentProvider(insuranceProviderList.find(p => p.name == insurance.providerName));
        setProviderName(insurance.providerName || '');
    };

    const handleSetForNew = () => {
        setEditingInsurance(null);
        setProviderName('');
        setNewInsurance({
            providerName: '',
            type: InsuranceType.MEDICAL,
            status: InsuranceStatus.ACTIVE,
            policyHolder: InsurancePolicyHolder.SELF,
            memberFirstName: patient?.firstName,
            memberLastName: patient?.lastName,
            memberId: '',
            payerId: '',
            groupId: '',
            providerId: '',
            isPrimary: false,
            created: DateTime.now().toISO()
        });
    }

    const createCheckId = (insurance:Insurance) => {
        return `${insurance._id}${insurance.created}${insurance.updated}${insurance.providerName}${insurance.memberId}`
    }

    const handleAdd = () => {
        let newList = [...insurances];
        if (editingInsurance) {
            // Save edited insurance
            const eiId = createCheckId(editingInsurance);
            newList = newList.map((item) =>
                createCheckId(item) === eiId ? { ...newInsurance, providerName: providerName } : item
            );
        } else {
            // Add new insurance
            newList = [...insurances, { ...newInsurance,
                providerName: providerName,
                patientId: patient?._id || undefined,
                clientId: patient?.clientId || undefined,
                created: DateTime.now().toISO()
            }];
        }
        setEditingInsurance(null);
        setInsurances(newList);
        handleSetForNew();
        onUpdate && onUpdate(newList || []);
    };

    const handleDelete = (insurance:Insurance) => {
        const insId = createCheckId(insurance);
        const list = insurances.filter((item) => createCheckId(item) !== insId)
        setInsurances(list);
        setEditingInsurance(null);
        handleSetForNew();
        onUpdate && onUpdate(list || []);
    };

    const handleInputChange = (field: keyof Insurance,value: string|number|boolean|undefined) => {
        setNewInsurance({ ...newInsurance, [field]: value });
    };

    const isNewInsuranceValid = () => {
        if(!newInsurance) {
            return false;
        }
        const fn = newInsurance?.memberFirstName?.trim()?.length || 0;
        const ln = newInsurance?.memberLastName?.trim()?.length || 0;
        const pr = providerName?.trim()?.length || 0;
        const mi = newInsurance?.memberId?.trim()?.length && isMemberIdValid(newInsurance.memberId, newInsurance.type);
        return fn != 0 && ln != 0 && pr != 0 && mi != 0;
    }

    const isMemberIdValid = (memberId:string|undefined, insurancetype:string|undefined) => {
        if(!memberId || !insurancetype) {
            return false;
        }
        if(insurancetype == InsuranceType.MEDICARE) {
            return isValidMedicareId(memberId);
        }
        return true;
    }

    return (
        <Box p={1} border={hasBorder ? 1 : undefined} sx={{ overflowX: 'auto', width: '100%', }}>
            <Typography variant="h6" gutterBottom>{title || 'Current'}</Typography>
            {
                <List sx={{ minWidth: '400px' }}>
                    <ListItem key={'kst1'} divider>
                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs={2.5}><Typography variant={size} style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical' }}><b>Provider</b></Typography></Grid>
                            <Grid item xs={2.5}><Typography variant={size} style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical' }}><b>Member</b></Typography></Grid>
                            <Grid item xs={2}><Typography variant={size} style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical' }}><b>Member ID</b></Typography></Grid>
                            <Grid item xs={2}><Typography variant={size} style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical' }}><b>Policy Holder</b></Typography></Grid>
                            <Grid item xs={1.8}><Typography variant={size} style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical' }}><b>Type</b></Typography></Grid>
                            <Grid item xs={0.8}><Typography variant={size} style={{ overflow: 'visible', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical' }}><b>Primary</b></Typography></Grid>
                            <Grid item xs={0.4}></Grid>
                        </Grid>
                    </ListItem>
                    {insurances.map((insurance, index) => (
                        <ListItem key={'kks' + index} divider>
                            <ListItemButton onClick={() => handleEdit(insurance)}>
                                <Grid container spacing={1} alignItems="center">
                                    <Grid item xs={2.5}><Typography variant={size}>{insurance.providerName}</Typography></Grid>
                                    <Grid item xs={2.5}>
                                        <Typography variant={size}>{`${insurance.memberLastName}, ${insurance.memberFirstName}`}</Typography>
                                    </Grid>
                                    <Grid item xs={2}><Typography variant={size}>{insurance.memberId}</Typography></Grid>
                                    <Grid item xs={2}><Typography variant={size}>{insurance.policyHolder}</Typography></Grid>
                                    <Grid item xs={1.8}><Typography variant={size}>{insurance.type}</Typography></Grid>
                                    <Grid item xs={0.8}>
                                        <DoneIcon color="success" sx={{ mt: 0.25, visibility: insurance.isPrimary ? undefined : 'hidden' }} />
                                    </Grid>
                                    <Grid item xs={0.4}>
                                        {
                                            readonly
                                            ? <></>
                                            : <IconButton onClick={(e) => {
                                                handleDelete(insurance);
                                                e.stopPropagation();
                                            }} edge="end"><DeleteIcon /></IconButton>
                                        }
                                    </Grid>
                                </Grid>
                            </ListItemButton>
                        </ListItem>
                    ))}
                    {
                        !readonly ? <ListItem key={'kks-new'} divider>
                            <ListItemButton onClick={handleSetForNew}>
                                <Box display="flex" alignItems="center">
                                    <Box sx={{ minWidth: 60, mr:'8px' }}>
                                        <AddIcon />
                                    </Box>
                                    <Typography>{'Add New Insurance'}</Typography>
                                </Box>
                            </ListItemButton>
                        </ListItem>
                        : (!insurances || insurances?.length == 0) && <ListItem key={'kks-none'} divider>
                            <Grid container spacing={1} alignItems="center">
                                <Grid item xs={12}><Typography>{'No Insurance Available'}</Typography></Grid>
                            </Grid>
                        </ListItem>
                    }
                </List>
            }

            {
                readonly ? <></>
                :
                (<Typography variant="subtitle1" gutterBottom mt={2}>
                    {editingInsurance ? 'Edit Insurance' : 'New Insurance'}
                </Typography>)
            }
            {
                readonly ? <></>
                :
                (<Grid container spacing={2} sx={{ minWidth: '400px' }}>
                    <Grid item xs={5}>
                        <TextField
                            label="Member First Name"
                            fullWidth
                            value={newInsurance.memberFirstName}
                            error={!newInsurance.memberFirstName || newInsurance.memberFirstName?.trim().length == 0}
                            onChange={(e) => handleInputChange('memberFirstName', e.target.value)}
                            sx={{
                                '& .MuiFormLabel-root.Mui-error': { color: '#17A2C6', },
                                '& .MuiInput-underline:after': { borderBottomColor: '#17A2C6', },
                                '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#17A2C6', }
                            }}
                        />
                    </Grid>
                    <Grid item xs={5}>
                        <TextField
                            label="Member Last Name"
                            fullWidth
                            error={!newInsurance.memberLastName || newInsurance.memberLastName?.trim().length == 0}
                            value={newInsurance.memberLastName}
                            onChange={(e) => handleInputChange('memberLastName', e.target.value)}
                            sx={{
                                '& .MuiFormLabel-root.Mui-error': { color: '#17A2C6', },
                                '& .MuiInput-underline:after': { borderBottomColor: '#17A2C6', },
                                '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#17A2C6', }
                            }}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <FormControl fullWidth>
                            <InputLabel>Policy Holder</InputLabel>
                            <Select
                                disabled={newInsurance.type == InsuranceType.MEDICARE}
                                value={newInsurance.policyHolder}
                                onChange={(e) => handleInputChange('policyHolder', e.target.value)}
                                label="Policy Holder"
                            >
                                {InsurancePolicyHolderMap.map((holder) => (
                                    <MenuItem key={holder[0]} value={holder[0]}>
                                        {holder[1]}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <Autocomplete
                            freeSolo
                            options={insuraceProviderNames}
                            loading={insuranceProviderQuery.isLoading}
                            value={providerName}
                            onChange={(_e,v,r) => {
                                if (r === 'selectOption') {
                                    const newProvider = insuranceProviderList.find((p) => p.name == v);
                                    setCurrentProvider(newProvider||undefined);
                                    handleInputChange('payerId', newProvider?.payer_id)
                                } else if (r === 'createOption') {
                                    setCurrentProvider(undefined);
                                    handleInputChange('payerId', '');
                                }
                                setProviderName(v||'')
                            }}
                            onInputChange={(_e, v, r) => {
                                if(r == 'input') {
                                    setCurrentProvider(undefined);
                                    handleInputChange('payerId', '');
                                }
                                setProviderName(v||'')
                            }}
                            renderInput={(params) => (
                                <TextField
                                error={!providerName || providerName.trim().length == 0}
                                {...params}
                                label="Provider Name"
                                sx={{
                                    '& .MuiFormLabel-root.Mui-error': { color: '#17A2C6', },
                                    '& .MuiInput-underline:after': { borderBottomColor: '#17A2C6', },
                                    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#17A2C6', }
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                    <>
                                        {insuranceProviderQuery.isLoading ? <CircularProgress size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                    ),
                                }}
                                />
                            )}
                            filterOptions={(options, { inputValue }) =>
                                options.filter((option) =>
                                option.toLowerCase().includes(inputValue.toLowerCase()))
                            }
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                        <TextField
                            label="Payer ID"
                            fullWidth
                            value={newInsurance.payerId}
                            onChange={(e) => handleInputChange('payerId', e.target.value)}
                            InputLabelProps={{shrink:!!newInsurance.payerId}}
                            disabled={!!currentProvider}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <FormControl fullWidth>
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={newInsurance.type}
                                onChange={(e) => {
                                    const typeVal = e.target.value;
                                    if(typeVal !== InsuranceType.MEDICAL) {
                                        setCurrentProvider(undefined);
                                        setProviderName(InsuranceTypeMap.find(e => e[0] == typeVal)?.[1] || typeVal);
                                    } else {
                                        setProviderName('');
                                        setCurrentProvider(undefined);
                                    }
                                    if(typeVal == InsuranceType.MEDICARE) {
                                        handleInputChange('policyHolder',InsurancePolicyHolder.SELF);
                                    }
                                    handleInputChange('type', typeVal);
                                }}
                                label="Type"
                            >
                                {InsuranceTypeMap.map((type) => (
                                    <MenuItem key={type[0]} value={type[0]}>{type[1]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={6}>
                        <TextField
                            label="Member ID"
                            fullWidth
                            value={newInsurance.memberId}
                            error={!newInsurance.memberId || newInsurance.memberId?.trim().length == 0
                                || !isMemberIdValid(newInsurance.memberId, newInsurance.type)}
                            onChange={(e) => handleInputChange('memberId', e.target.value)}
                            InputLabelProps={{shrink:!!newInsurance.memberId}}
                            sx={{
                                '& .MuiFormLabel-root.Mui-error': { color: '#17A2C6', },
                                '& .MuiInput-underline:after': { borderBottomColor: '#17A2C6', },
                                '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#17A2C6', }
                            }}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            label="Group ID"
                            fullWidth
                            value={newInsurance.groupId || ''}
                            onChange={(e) => handleInputChange('groupId', e.target.value)}
                            InputLabelProps={{shrink:!!newInsurance.groupId}}
                        />
                    </Grid>
                    <Grid item xs={2} alignSelf={'center'}>
                        <Box border={1} borderRadius={1} padding="6px" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={newInsurance.isPrimary ?? false}
                                        onChange={(e) => handleInputChange('isPrimary', e.target.checked)}
                                    />
                                }
                                label="Primary"
                                sx={{ overflow: 'visible', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}
                            />
                            </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Button variant="contained" color="primary" onClick={handleAdd} fullWidth
                            sx={{width:'25%', maxWidth:'100%', minWidth:'300px'}}
                            disabled={!isNewInsuranceValid()}>
                            { editingInsurance ? 'Update Insurance' : 'Add Insurance' }
                        </Button>
                    </Grid>
                </Grid>)
            }
        </Box>
    );
};

export default InsuranceList;

